'use client'

import * as React from 'react'
import { ArrowRight, Heart } from 'lucide-react'
import { cn, formatPrice, proxyImageUrl } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'
import { IconBtn, PhotoBlock, fmtCZK, isLikelyMonthlyPrice, paletteFor } from '@/components/roostr'
import { useSaveListing } from './use-save-listing'
import type { Listing } from '@/lib/types'

// Straight-line distance to the city centre (Staroměstské nám.) — ported
// exactly from design search.jsx:22-27.
const CENTRE = { lat: 50.0875, lon: 14.4213 }
export function kmToCentre(lat: number, lon: number): number {
  const dLat = (lat - CENTRE.lat) * 111.2
  const dLon = (lon - CENTRE.lon) * 111.2 * Math.cos((50.08 * Math.PI) / 180)
  return Math.sqrt(dLat * dLat + dLon * dLon)
}

// Map-view row card — design search.jsx:31-72. The user is asking "where
// would I live?", so the district leads, distance to centre anchors, and the
// flat itself is the supporting line.
export interface MapRowCardProps {
  listing: Listing
  active?: boolean
  onHover: (id: string | null) => void
  /**
   * Click = "show me this one on the map" — centre it and open its mini-card.
   * The listing page then opens from that card's Details button. The panel is
   * for navigating the map (user 2026-08-16); it used to open the listing in
   * a new tab, which is a much heavier action than a single click deserves.
   * Falls back to the old behaviour when no handler is given.
   */
  onSelect?: (listing: Listing) => void
}

// Memoized: the district list re-renders on every hover/selection change and
// only the affected rows' props actually change.
export const MapRowCard = React.memo(function MapRowCard({ listing: l, active, onHover, onSelect }: MapRowCardProps) {
  const { t } = useI18n()
  const { isSaved, isSaving, toggleSave } = useSaveListing(l.id)
  const [imgFailed, setImgFailed] = React.useState(false)

  // Currency-aware price (same pattern as pay-card.tsx) — fmtCZK would
  // mislabel the odd EUR listing.
  const fmt = (n: number) =>
    !l.price_currency || l.price_currency === 'CZK' ? fmtCZK(n) : formatPrice(n, l.price_currency)

  const img = proxyImageUrl(l.images?.[0]?.url)
  const km = l.location?.lat && l.location?.lon ? kmToCentre(l.location.lat, l.location.lon) : null
  const streetTitle = [l.street, l.title].filter(Boolean).join(' · ')
  const specs = [
    typeof l.rooms === 'number' ? `${l.rooms}+kk` : null,
    typeof l.area === 'number' ? `${l.area} m²` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div
      onClick={() =>
        onSelect ? onSelect(l) : window.open(`/listings/${l.id}`, '_blank', 'noopener,noreferrer')
      }
      onMouseEnter={() => onHover(l.id)}
      onMouseLeave={() => onHover(null)}
      className={cn(
        'relative flex cursor-pointer gap-[11px] rounded-shape-md border p-[10px]',
        'transition-[background-color,border-color,box-shadow,transform] duration-150',
        active
          ? // The map/list pairing only works if "this is the one" is
            // unmissable at a glance. A 1px coral border on white was not
            // (user 2026-08-16). Four cues at once, none of which shift
            // layout: coral border, tinted fill, coral halo ring, and a lift.
            'z-10 border-primary bg-primary-container shadow-e2 ring-2 ring-primary/40'
          : 'border-outline-variant bg-surface hover:border-outline'
      )}
    >
      <div className="h-[62px] w-[62px] flex-shrink-0 overflow-hidden rounded-shape-xs">
        {img && !imgFailed ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={img}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <PhotoBlock palette={paletteFor(l.id)} className="h-full w-full" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[13.5px] font-bold">{l.district || 'Praha'}</span>
          {km != null && (
            <span
              title={t('search.kmToCentreTitle', 'Straight-line distance to the city centre')}
              className="ml-auto inline-flex items-center gap-1 whitespace-nowrap font-mono text-[11px] font-semibold text-ink-muted"
            >
              {km.toFixed(1)} {t('search.km', 'km')}
              <ArrowRight className="h-3 w-3 shrink-0" aria-hidden />
              {t('search.centre', 'centre')}
            </span>
          )}
        </div>
        <div className="mt-[1px] overflow-hidden text-ellipsis whitespace-nowrap text-[12px] text-ink-secondary">
          {streetTitle}
        </div>
        <div className="mt-[5px] flex items-baseline gap-[5px]">
          {/* On the coral-tinted active card the coral price drops to ~3:1
              contrast — below AA for the card's most important number.
              `on-primary-container` is the token for text on that surface
              (~11:1). */}
          <span
            className={cn(
              'font-mono text-[13.5px] font-bold',
              active ? 'text-on-primary-container' : l.price > 0 ? 'text-primary' : 'text-ink-muted'
            )}
          >
            {l.price > 0 ? fmt(l.price) : t('listingCard.noPrice', 'No price')}
          </span>
          {l.price > 0 && isLikelyMonthlyPrice(l.price) && (
            <span className="text-[11px] text-ink-muted">{t('search.perMonth', '/mo')}</span>
          )}
          {specs && (
            <span className="ml-auto whitespace-nowrap text-[11.5px] font-semibold text-ink-secondary">
              {specs}
            </span>
          )}
        </div>
      </div>

      <IconBtn
        size={30}
        onClick={toggleSave}
        title={isSaved ? t('search.unlike', 'Remove from liked') : t('search.like', 'Like')}
        className="self-center bg-surface-low"
        style={{ opacity: isSaving ? 0.6 : undefined }}
      >
        <Heart
          size={15}
          fill={isSaved ? 'currentColor' : 'none'}
          className={isSaved ? 'text-primary' : 'text-ink-secondary'}
        />
      </IconBtn>
    </div>
  )
})
